"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { useUser } from "@auth0/nextjs-auth0/client";
import { useRouter, useSearchParams } from "next/navigation";

import type { ApiError, ApiResponse, RequestConfig } from "@/types/api";
import { apiClient } from "@/utils/api-client";

interface UseApiOptions {
  onError?: (error: ApiError) => void;
  onSuccess?: <T>(data: T) => void;
  withAuth?: boolean;
}

export function useApi(options: UseApiOptions = {}) {
  const { user, error: authError, isLoading: authLoading } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const activeRequests = useRef(new Map<string, AbortController>());

  // Cleanup function for active requests
  useEffect(() => {
    const currentRequests = activeRequests.current;

    return () => {
      currentRequests.forEach(controller => controller.abort());
      currentRequests.clear();
    };
  }, []);

  const request = useCallback(async <T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> => {
    if (options.withAuth && !user) {
      router.push(`/api/auth/login?${searchParams.toString()}`);
      throw new Error("Authentication required");
    }

    const requestId = crypto.randomUUID();
    const controller = new AbortController();
    activeRequests.current.set(requestId, controller);

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.request<T>(endpoint, {
        ...config,
        signal: controller.signal,
      });

      if (response.error) {
        setError(response.error);
        options.onError?.(response.error);

        // Handle specific error cases
        switch (response.status) {
        case 401:
          router.push(`/api/auth/login?${searchParams.toString()}`);
          break;
        default:
          router.push(`/api/auth/login?${searchParams.toString()}`);
        }
      } else {
        options.onSuccess?.(response.data as T);
      }

      return response;
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError);
      options.onError?.(apiError);
      throw apiError;
    } finally {
      setIsLoading(false);
      activeRequests.current.delete(requestId);
    }
  }, [user, router, options]);

  const cancelRequest = useCallback((requestId: string) => {
    const controller = activeRequests.current.get(requestId);
    if (controller) {
      controller.abort();
      activeRequests.current.delete(requestId);
    }
  }, []);

  const cancelAllRequests = useCallback(() => {
    activeRequests.current.forEach(controller => controller.abort());
    activeRequests.current.clear();
  }, []);

  return {
    request,
    cancelRequest,
    cancelAllRequests,
    isLoading: isLoading || authLoading,
    error: error || authError,
    rateLimitInfo: apiClient.getRateLimitInfo(),
  };
}
